import Group from 'components/utils/Group'
import NumberRating from 'components/Common/NumberRating'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import Divider from 'LuxKit/Divider'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import styled from 'styled-components'
import { rem } from 'polished'
import {
  OfferDetailImageGallery,
  OfferDetailDescription,
  OfferDetailReviews,
  OfferDetailMap,
} from '../OfferDetail'
import type { OfferDetailReview, OfferDetailGeoLocation } from '../OfferDetail'
import type { ExperienceDetailResult } from 'tripPlanner/api/types'
import ExperienceDetailMetadata from './ExperienceDetailMetadata'
import ExperienceDetailPricing from './ExperienceDetailPricing'
import getDrawerBookableUntilDate from './getDrawerBookableUntilDate'

const ContentWrapper = styled.div`
  margin: 0 auto;
  padding: ${rem(24)};
  width: ${rem(700)};
  maxwidth: 100%;
  box-sizing: border-box;
`

const CategoryLabel = styled(BodyText)`
  text-transform: capitalize;
`

export type DrawerExperience =
  Omit<ExperienceDetailResult, 'images' | 'redemptionLocations' | 'geoLocation'> & {
    images: Array<App.Image>
    geoLocation?: OfferDetailGeoLocation
  }

export type DrawerExperienceSearchTile = DrawerExperience & Partial<Pick<App.ExperienceOffer, 'copy' | 'giftType' | 'productType'>>

interface Props {
  experience: DrawerExperience
  standaloneOffer?: Pick<App.ExperienceOffer, 'id' | 'expirationDate' | 'location'>
  reviews?: Array<OfferDetailReview>
  reviewsLoading?: boolean
}

function ExperienceDetailView({
  experience,
  standaloneOffer,
  reviews,
  reviewsLoading,
}: Props) {
  const {
    name,
    location,
    description,
    images,
    rating,
    reviewsTotal,
    geoLocation,
    deeplinkUrl,
    price,
    memberPrice,
    memberDiscountPercent,
    value,
    currencyCode,
    duration,
    categories,
    isSoldOut,
  } = experience

  const hasDescription = !!description
  const hasReviews = !!reviews && reviews.length > 0
  const hasGeoLocation = !!geoLocation
  const categoryLabels = categories.map((c) => c.label).join(', ')
  const bookableUntilDate = getDrawerBookableUntilDate(standaloneOffer)

  return (
    <ContentWrapper>
      <Group direction="vertical" gap={24}>
        <Group direction="vertical" gap={12}>
          <Group direction="vertical">
            <Heading variant="heading2">{name}</Heading>
            <BodyText variant="medium" colour="light-foreground-alt">{location}</BodyText>
            {categoryLabels && (
              <CategoryLabel variant="small" colour="light-foreground-alt">{categoryLabels}</CategoryLabel>
            )}
          </Group>
          {rating !== undefined && rating > 0 && (
            <NumberRating
              rating={rating}
              total={reviewsTotal}
              variant="small"
            />
          )}
        </Group>

        <OfferDetailImageGallery images={images} />

        <ExperienceDetailMetadata
          duration={duration}
          bookableUntilDate={bookableUntilDate}
        />

        {hasDescription && (
          <>
            <Divider kind="secondary" />
            <OfferDetailDescription description={description} />
          </>
        )}

        <Divider kind="secondary" />

        <ExperienceDetailPricing
          price={price}
          memberPrice={memberPrice ?? undefined}
          memberDiscountPercent={memberDiscountPercent ?? undefined}
          value={value}
          currencyCode={currencyCode}
          deeplinkUrl={deeplinkUrl}
          isSoldOut={isSoldOut}
        />

        {(hasReviews || reviewsLoading) && (
          <>
            <Divider kind="secondary" />
            {reviewsLoading && !hasReviews && (
              <Group direction="vertical" gap={12}>
                <LoadingBox height={20} width={120} borderRadius="S" />
                <LoadingBox height={60} width={320} borderRadius="S" />
              </Group>
            )}
            {hasReviews && (
              <OfferDetailReviews
                rating={rating}
                reviewsTotal={reviewsTotal}
                reviews={reviews}
              />
            )}
          </>
        )}

        {hasGeoLocation && (
          <>
            <Divider kind="secondary" />
            <OfferDetailMap
              geoLocation={geoLocation}
              offerName={name}
            />
          </>
        )}
      </Group>
    </ContentWrapper>
  )
}

export default ExperienceDetailView
